import React, { useState, useRef, useEffect } from "react";
import { buildRecurrenceRule, describeRecurrence, RECURRENCE_PRESETS } from "../../utils/recurrenceEngine";

const FREQS = [
  { value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" }, { value: "yearly", label: "Yearly" },
];
const DAYS = [
  { code: "MO", label: "Mon" }, { code: "TU", label: "Tue" }, { code: "WE", label: "Wed" },
  { code: "TH", label: "Thu" }, { code: "FR", label: "Fri" }, { code: "SA", label: "Sat" },
  { code: "SU", label: "Sun" },
];

/**
 * RecurrencePicker — pill + dropdown for setting recurrence rules.
 * Props: value (RecurrenceRule|null), onChange (rule|null => void), dropdownAlign ("left"|"right")
 */
export function RecurrencePicker({ value, onChange, dropdownAlign = "left" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [mode, setMode] = useState("every");
  const [frequency, setFrequency] = useState("daily");
  const [interval, setInterval] = useState(1);
  const [byDay, setByDay] = useState([]);
  const [byMonthDay, setByMonthDay] = useState("");
  const [endType, setEndType] = useState("never");
  const [endDate, setEndDate] = useState("");
  const [maxOccurrences, setMaxOccurrences] = useState(30);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value && value.isRecurring) {
      setMode(value.mode || "every");
      setFrequency(value.frequency || "daily");
      setInterval(value.interval || 1);
      setByDay(value.byDay || []);
      setByMonthDay(value.byMonthDay ? value.byMonthDay.join(", ") : "");
      setEndType(value.endType || (value.endDate ? "on_date" : value.maxOccurrences ? "after_occurrences" : "never"));
      setEndDate(value.endDate || "");
      setMaxOccurrences(value.maxOccurrences || 30);
    }
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setShowCustom(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isSet = value && value.isRecurring;
  const pillText = isSet ? describeRecurrence(value) : "Repeat";
  const pillClass = isSet
    ? "text-indigo-600 bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
    : "text-gray-500 hover:bg-gray-100 border-gray-300";

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
    setShowCustom(false);
  };

  const handlePreset = (preset) => {
    onChange(buildRecurrenceRule(preset.rule));
    setIsOpen(false);
    setShowCustom(false);
  };

  const handleApplyCustom = () => {
    const parsed = byMonthDay.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => n >= 1 && n <= 31);
    onChange(buildRecurrenceRule({
      mode, frequency, interval,
      byDay: frequency === "weekly" ? byDay : undefined,
      byMonthDay: frequency === "monthly" ? parsed : undefined,
      endType,
      endDate: endType === "on_date" ? endDate : undefined,
      maxOccurrences: endType === "after_occurrences" ? maxOccurrences : undefined,
      occurrenceCount: value?.occurrenceCount || 1,
    }));
    setIsOpen(false);
    setShowCustom(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-200 ${pillClass}`}
      >
        <span>🔄</span>
        <span>{pillText}</span>
        {isSet && (
          <span
            onClick={handleClear}
            className="ml-1 text-xs hover:bg-black/10 rounded-full w-4 h-4 flex items-center justify-center font-bold text-gray-500 transition-colors"
            title="Clear recurrence"
          >×</span>
        )}
      </button>

      {isOpen && (
        <div className={`absolute z-50 mt-1 ${dropdownAlign === "right" ? "right-0" : "left-0"} w-72 bg-white border border-gray-200 rounded-lg shadow-lg py-1 animate-fadeIn`}>
          {RECURRENCE_PRESETS.map((p) => (
            <button
              key={p.label} type="button" onClick={() => handlePreset(p)}
              className="w-full text-left px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <span className="text-indigo-400">🔄</span>
              <span>{p.label}</span>
            </button>
          ))}

          <div className="border-t border-gray-100 my-1" />

          <button
            type="button" onClick={() => setShowCustom(!showCustom)}
            className="w-full text-left px-3 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50"
          >{showCustom ? "▾ Custom…" : "▸ Custom…"}</button>

          {showCustom && (
            <div className="px-3 pb-2 space-y-2">
              {/* Mode toggle */}
              <div>
                <div className="flex gap-1 mb-0.5">
                  {["every", "every!"].map((m) => (
                    <button key={m} type="button" onClick={() => setMode(m)}
                      className={`px-2 py-1 text-[10px] font-bold rounded ${mode === m ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-500"}`}
                    >{m}</button>
                  ))}
                </div>
                <p className="text-[9px] text-gray-400">every = from scheduled date, every! = from completion date</p>
              </div>

              {/* Frequency + Interval */}
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold text-gray-500">Every</span>
                <input type="number" min={1} max={99} value={interval}
                  onChange={(e) => setInterval(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                  className="w-12 text-xs px-1.5 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <select value={frequency} onChange={(e) => setFrequency(e.target.value)}
                  className="text-xs px-1.5 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-700"
                >
                  {FREQS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>

              {/* Day-of-week checkboxes (weekly only) */}
              {frequency === "weekly" && (
                <div className="flex flex-wrap gap-1">
                  {DAYS.map((d) => (
                    <button key={d.code} type="button" onClick={() => setByDay((prev) =>
                      prev.includes(d.code) ? prev.filter((c) => c !== d.code) : [...prev, d.code]
                    )}
                      className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${byDay.includes(d.code) ? "bg-indigo-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                    >{d.label}</button>
                  ))}
                </div>
              )}

              {/* Day-of-month input (monthly only) */}
              {frequency === "monthly" && (
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase mb-0.5">Days of month</label>
                  <input type="text" value={byMonthDay} onChange={(e) => setByMonthDay(e.target.value)}
                    placeholder="e.g. 1, 15"
                    className="w-full text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-700"
                  />
                </div>
              )}

              {/* Ends option */}
              <div className="border-t border-gray-100 pt-1.5">
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Ends</label>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="endType" value="never" checked={endType === "never"} onChange={() => setEndType("never")} />
                    <span className="text-xs text-gray-700">Never</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="endType" value="on_date" checked={endType === "on_date"} onChange={() => setEndType("on_date")} />
                    <span className="text-xs text-gray-700">On date</span>
                    {endType === "on_date" && (
                      <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                        className="text-xs px-1.5 py-0.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 ml-1 text-gray-700"
                      />
                    )}
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="endType" value="after_occurrences" checked={endType === "after_occurrences"} onChange={() => setEndType("after_occurrences")} />
                    <span className="text-xs text-gray-700">After</span>
                    {endType === "after_occurrences" && (
                      <div className="flex items-center gap-1 ml-1">
                        <input type="number" min={1} max={999} value={maxOccurrences} onChange={(e) => setMaxOccurrences(Math.max(1, parseInt(e.target.value, 10) || 1))}
                          className="w-12 text-xs px-1.5 py-0.5 border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-700"
                        />
                        <span className="text-[11px] text-gray-500">occurrences</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <button type="button" onClick={handleApplyCustom}
                className="w-full py-1.5 text-xs font-semibold text-white bg-indigo-500 rounded hover:bg-indigo-600 transition-colors mt-2"
              >Apply</button>
            </div>
          )}

          <div className="border-t border-gray-100 my-1" />
          <button type="button" onClick={handleClear}
            className="w-full text-left px-3 py-2 text-xs font-medium text-red-500 hover:bg-red-50 flex items-center gap-2"
          ><span>✕</span><span>No repeat</span></button>
        </div>
      )}
    </div>
  );
}
